import type { MatchState, TeamSide } from "@golazo/shared";

/**
 * Los rotulos de "retransmision" de un partido contra la IA: que se VEA lo que
 * decide la IA (2026-09-29).
 *
 * La IA lee el marcador y el reloj (`ai/situation.ts`): perdiendo al final se
 * vuelca, ganando se encierra y su portero pierde tiempo. Eso cambia el partido
 * —un rival volcado deja la espalda al descubierto— pero el jugador no tenia
 * forma de saberlo: parecia que la IA jugaba "raro". Esto decide CUANDO
 * contarlo; como se cuenta lo pone la interfaz.
 *
 * Solo lectura: mira el estado y la urgencia, no toca nada de la simulacion.
 */
export type CaptionKind = "chase" | "protect" | "style";

export interface CaptionEvent {
  kind: CaptionKind;
  side: TeamSide;
}

export interface CommentaryInput {
  state: Pick<MatchState, "phase" | "goalPause" | "breakPause">;
  /** Lo que pide el partido a cada equipo (`TeamTacticsState.urgency`). */
  urgency: Record<TeamSide, number>;
  /** Segundos de reloj real desde el anterior `update`. */
  dt: number;
}

/** Urgencia a partir de la cual se cuenta, y por debajo de la cual se rearma. */
export const CAPTION_ON = 0.5;
export const CAPTION_RESET = 0.25;
/** Segundos de juego antes de presentar el estilo del rival: que se vea el saque. */
export const STYLE_DELAY = 1.5;

export function createCommentary(options: {
  /** Presentar el estilo de este equipo al empezar (la carrera lo sabe). */
  styleSide?: TeamSide;
}) {
  /** Lo ultimo contado de cada equipo: 1 volcado, -1 encerrado, 0 nada. */
  const told: Record<TeamSide, -1 | 0 | 1> = { home: 0, away: 0 };
  let styleLeft = options.styleSide ? STYLE_DELAY : Infinity;

  return {
    /**
     * Lo que hay que contar ahora. Nunca durante una pausa (gol, repeticion,
     * cuenta atras, descanso): un rotulo encima de la repeticion taparia justo
     * lo que se esta mirando, y el cambio de plan tras un gol se cuenta al
     * volver a jugar.
     */
    update(input: CommentaryInput): CaptionEvent[] {
      const { state } = input;
      if (state.phase !== "playing" || state.goalPause > 0 || state.breakPause > 0) return [];
      const out: CaptionEvent[] = [];

      if (options.styleSide && styleLeft !== Infinity) {
        styleLeft -= input.dt;
        if (styleLeft <= 0) {
          styleLeft = Infinity;
          out.push({ kind: "style", side: options.styleSide });
        }
      }

      for (const side of ["home", "away"] as const) {
        const u = input.urgency[side];
        // Se rearma en silencio: volver a jugar tranquilo no es noticia.
        if (told[side] === 1 && u < CAPTION_RESET) told[side] = 0;
        if (told[side] === -1 && u > -CAPTION_RESET) told[side] = 0;
        if (u >= CAPTION_ON && told[side] !== 1) {
          told[side] = 1;
          out.push({ kind: "chase", side });
        } else if (u <= -CAPTION_ON && told[side] !== -1) {
          told[side] = -1;
          out.push({ kind: "protect", side });
        }
      }
      return out;
    },
  };
}
